import { useRef, useState, useEffect, type ReactNode, type CSSProperties } from "react";

interface ExpandableTextProps {
  children: ReactNode;
  /** Number of lines to show when collapsed. Default: 3 */
  lines?: number;
  className?: string;
}

/**
 * Text container that truncates to a set number of lines with a "read more" toggle.
 * Automatically detects if truncation is needed based on content height.
 */
export function ExpandableText({ children, lines = 3, className }: ExpandableTextProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkTruncation = () => {
      // Compare scroll height to client height to detect truncation
      setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    };

    checkTruncation();

    // Re-check on resize in case container width changes
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  // Use inline styles for line-clamp since Tailwind can't generate dynamic class names
  const clampStyle: CSSProperties = isExpanded
    ? {}
    : {
        display: "-webkit-box",
        WebkitLineClamp: lines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      };

  return (
    <div className={className}>
      <div ref={contentRef} style={clampStyle}>
        {children}
      </div>
      {(isTruncated || isExpanded) && (
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-primary hover:underline cursor-pointer"
          >
            {isExpanded ? "Show less" : "Read more"}
          </button>
        </div>
      )}
    </div>
  );
}
